import type { Event, EventType, Venue, Artist, LineupEntry, Set as FestivalSet } from './firestore'
import { eventRegionId } from './regions'
import { entryInterestIds } from './lineupActs'

// The global search index: a public Storage artifact built by the
// rebuildArtifacts Cloud Function and searched client-side. It holds only
// already-public data — published events, their lineups and sets, artist names.
export const SEARCH_INDEX_SCHEMA = 1
export const SEARCH_ARTIFACT_KEY = `search@${SEARCH_INDEX_SCHEMA}`

// Tuples keep the gzipped file near the ~300 KB target; '' = absent. `image`
// is a trailing optional so files built before it existed still decode.
export type EncodedEvent = [
  id: string, type: 'f' | 'p', name: string, venueName: string, city: string,
  startDate: string, endDate: string, firstSetDay: string, lastSetDay: string, regionId: string, cancelled: 0 | 1,
  image?: string,
]
export type EncodedArtist = [
  id: string, name: string, aliases: string[], lineupEventIdx: number[], memberIdx: number[], image?: string,
]
export type EncodedSet = [
  id: string, eventIdx: number, day: string, startTime: string, stageName: string, artistIdx: number[], title: string,
]

export interface SearchIndexFile {
  schema: typeof SEARCH_INDEX_SCHEMA
  builtAt: string
  events: EncodedEvent[]
  artists: EncodedArtist[]
  sets: EncodedSet[]
}

export interface SearchEvent {
  id: string
  type: EventType
  name: string
  venueName?: string
  city?: string
  startDate: string
  endDate?: string
  firstSetDay?: string
  lastSetDay?: string
  regionId?: string
  cancelled: boolean
  imageUrl?: string
}

export interface SearchSet {
  id: string
  eventIdx: number
  day: string
  /** '' when the set has no start time (some seeded sets lack one). */
  startTime: string
  stageName?: string
  artistIdx: number[]
  title?: string
}

export interface SearchArtist {
  id: string
  name: string
  aliases: string[]
  imageUrl?: string
  /** Events the artist appears at, crediting composite (b2b) acts to their members. */
  eventIdx: number[]
  setIdx: number[]
  /** A named collab's members (Dog Blood → Boys Noize, Skrillex). */
  memberIdx: number[]
  /** The named collabs this artist is a member of ("Also performs as"). */
  collabIdx: number[]
}

export interface SearchIndex {
  version: string
  builtAt: string
  events: SearchEvent[]
  artists: SearchArtist[]
  sets: SearchSet[]
}

export interface SearchIndexInput {
  events: Event[]
  venues: Venue[]
  artists: Artist[]
  lineup: LineupEntry[]
  sets: FestivalSet[]
  /** Keyed by `${eventId}/${stageId}`. */
  stageNames: Map<string, string>
  builtAt: string
}

const STORAGE_URL = /^https:\/\/firebasestorage\.googleapis\.com\/v0\/b\/([^/]+)\/o\/([^?]+)/
const STORAGE_REF = 'fs:'

/**
 * Firebase Storage download URLs are mostly an unguessable token, which doesn't
 * compress — keeping them whole nearly doubled the index. artists/ and events/
 * are public-read, so `fs:<bucket>/<path>` is enough to rebuild a working URL.
 * Anything else (Instagram, Posh) is kept as is.
 */
export function compactImageUrl(url: string | undefined): string {
  if (!url) return ''
  const m = STORAGE_URL.exec(url)
  return m ? `${STORAGE_REF}${m[1]}/${decodeURIComponent(m[2])}` : url
}

export function expandImageUrl(ref: string | undefined): string | undefined {
  if (!ref) return undefined
  if (!ref.startsWith(STORAGE_REF)) return ref
  const rest = ref.slice(STORAGE_REF.length)
  const slash = rest.indexOf('/')
  const bucket = rest.slice(0, slash)
  const path = rest.slice(slash + 1)
  return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(path)}?alt=media`
}

export function normalizeSearchText(s: string): string {
  return s.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()
}

/** Extra spellings people type: without a "DJ "/"MC " prefix, and squashed to alphanumerics ("A-Trak" → "atrak"). */
export function deriveArtistAliases(name: string): string[] {
  const base = normalizeSearchText(name).trim()
  const out = new Set<string>()
  const unprefixed = base.replace(/^(dj|mc)\s+/, '')
  if (unprefixed !== base && unprefixed) out.add(unprefixed)
  for (const form of [base, unprefixed]) {
    const squashed = form.replace(/[^\p{L}\p{N}]+/gu, '')
    if (squashed && squashed !== form) out.add(squashed)
  }
  return [...out]
}

export function encodeSearchIndex(input: SearchIndexInput): SearchIndexFile {
  const venues = new Map(input.venues.map(v => [v.id, v]))
  const included = input.events.filter(e => e.published !== false)
  const eventIdx = new Map(included.map((e, i) => [e.id, i]))

  const events: EncodedEvent[] = included.map(e => {
    const venue = e.venueId ? venues.get(e.venueId) : undefined
    return [
      e.id, e.type === 'party' ? 'p' : 'f', e.name, e.venueName ?? venue?.name ?? '', venue?.city ?? e.location ?? '',
      e.startDate, e.endDate ?? '', e.firstSetDay ?? '', e.lastSetDay ?? '', eventRegionId(e, venue) ?? '', e.cancelled ? 1 : 0,
      compactImageUrl(e.imageUrl),
    ]
  })

  // Every artist with any lineup or set row — published or not — so admin
  // search still finds draft-lineup artists. Appearances below only reference
  // included events, so a draft-only artist ships with none.
  const artistsById = new Map(input.artists.map(a => [a.id, a]))
  const appearing = new Set<string>()
  const addAppearing = (id: string) => {
    const artist = artistsById.get(id)
    if (!artist || appearing.has(id)) return
    appearing.add(id)
    artist.memberArtistIds?.forEach(addAppearing)
  }
  input.lineup.forEach(l => entryInterestIds(l).forEach(addAppearing))
  input.sets.forEach(s => entryInterestIds(s).forEach(addAppearing))

  const kept = input.artists.filter(a => appearing.has(a.id))
  const artistIdx = new Map(kept.map((a, i) => [a.id, i]))

  const lineupEvents = new Map<string, Set<number>>()
  for (const l of input.lineup) {
    const ei = eventIdx.get(l.eventId)
    if (ei === undefined) continue
    for (const id of entryInterestIds(l)) {
      if (!artistIdx.has(id)) continue
      let set = lineupEvents.get(id)
      if (!set) lineupEvents.set(id, set = new Set())
      set.add(ei)
    }
  }

  const artists: EncodedArtist[] = kept.map(a => [
    a.id, a.name, deriveArtistAliases(a.name),
    [...(lineupEvents.get(a.id) ?? [])],
    (a.memberArtistIds ?? []).map(id => artistIdx.get(id)).filter((i): i is number => i !== undefined),
    compactImageUrl(a.imageUrl),
  ])

  const sets: EncodedSet[] = []
  for (const s of input.sets) {
    const ei = eventIdx.get(s.eventId)
    if (ei === undefined || s.placeholder) continue
    const idx = s.artistIds.map(id => artistIdx.get(id)).filter((i): i is number => i !== undefined)
    if (idx.length === 0) continue
    const stageName = s.stageId ? input.stageNames.get(`${s.eventId}/${s.stageId}`) ?? '' : ''
    // Seeded docs don't always honour the Set type — a missing startTime arrives as null.
    sets.push([s.id, ei, s.day ?? '', s.startTime ?? '', stageName, idx, s.title ?? ''])
  }

  return { schema: SEARCH_INDEX_SCHEMA, builtAt: input.builtAt, events, artists, sets }
}

export function decodeSearchIndex(file: SearchIndexFile, version: string): SearchIndex {
  const events: SearchEvent[] = file.events.map(
    ([id, type, name, venueName, city, startDate, endDate, firstSetDay, lastSetDay, regionId, cancelled, image]) => ({
      id, type: type === 'p' ? 'party' : 'festival', name,
      venueName: venueName || undefined, city: city || undefined, startDate,
      endDate: endDate || undefined, firstSetDay: firstSetDay || undefined, lastSetDay: lastSetDay || undefined,
      regionId: regionId || undefined, cancelled: cancelled === 1, imageUrl: expandImageUrl(image),
    }),
  )
  const sets: SearchSet[] = file.sets.map(([id, eventIdx, day, startTime, stageName, artistIdx, title]) => ({
    // ?? '' also repairs files built before the encoder normalised nulls.
    id, eventIdx, day: day ?? '', startTime: startTime ?? '', stageName: stageName || undefined, artistIdx, title: title || undefined,
  }))

  const eventSets = file.artists.map(([, , , lineupEventIdx]) => new Set(lineupEventIdx))
  const setSets = file.artists.map(() => new Set<number>())
  sets.forEach((s, si) => s.artistIdx.forEach(ai => {
    setSets[ai].add(si)
    eventSets[ai].add(s.eventIdx)
  }))
  // A composite act's appearances count for each member, so searching one
  // half of a b2b finds the shared set.
  file.artists.forEach(([, , , , memberIdx], ci) => {
    for (const mi of memberIdx) {
      eventSets[ci].forEach(e => eventSets[mi].add(e))
      setSets[ci].forEach(s => setSets[mi].add(s))
    }
  })

  const collabIdx = file.artists.map(() => [] as number[])
  file.artists.forEach(([, , , , memberIdx], ci) => memberIdx.forEach(mi => collabIdx[mi].push(ci)))

  const artists: SearchArtist[] = file.artists.map(([id, name, aliases, , memberIdx, image], i) => ({
    id, name, aliases, imageUrl: expandImageUrl(image), eventIdx: [...eventSets[i]], setIdx: [...setSets[i]],
    memberIdx, collabIdx: collabIdx[i],
  }))

  return { version, builtAt: file.builtAt, events, artists, sets }
}
